import React, { useState, useRef } from "react";
import { exportData, importData, getAllEntries, loadData } from "../api/mockData";

interface SettingsProps {
  onDataChange: () => void;
}

export function Settings({ onDataChange }: SettingsProps) {
  const [importStatus, setImportStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [showExportInfo, setShowExportInfo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const entries = getAllEntries();
  const data = loadData();

  const handleExport = () => {
    const json = exportData();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mood-diary-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportInfo(true);
    setTimeout(() => setShowExportInfo(false), 3000);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const result = importData(text);
      setImportStatus({
        type: result.success ? "success" : "error",
        message: result.message,
      });
      if (result.success) {
        onDataChange();
      }
      setTimeout(() => setImportStatus(null), 4000);
    };
    reader.readAsText(file);

    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClearAll = () => {
    if (window.confirm("Вы уверены, что хотите удалить все записи? Это действие нельзя отменить.")) {
      localStorage.removeItem("mood-diary-data");
      onDataChange();
      setImportStatus({ type: "success", message: "Все записи удалены" });
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  // Calculate storage size
  const storageSize = new Blob([JSON.stringify(data)]).size;
  const storageSizeStr = storageSize < 1024
    ? `${storageSize} Б`
    : storageSize < 1024 * 1024
    ? `${(storageSize / 1024).toFixed(1)} КБ`
    : `${(storageSize / (1024 * 1024)).toFixed(1)} МБ`;

  // Date range
  const firstEntry = entries[0];
  const lastEntry = entries[entries.length - 1];
  const dateRange = firstEntry && lastEntry
    ? `${new Date(firstEntry.date + "T00:00:00").toLocaleDateString("ru-RU")} — ${new Date(lastEntry.date + "T00:00:00").toLocaleDateString("ru-RU")}`
    : "Нет записей";

  return (
    <div className="animate-page-in pb-28">
      {/* Header */}
      <div className="px-5 pt-6 pb-3">
        <h1
          className="font-serif text-[2rem] font-extralight tracking-tight leading-tight"
          style={{ color: "var(--foreground)" }}
        >
          Данные
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--muted-foreground)" }}>
          Экспорт, импорт и управление записями
        </p>
      </div>

      {/* Status notification */}
      {importStatus && (
        <div
          className="mx-5 mb-4 px-4 py-3 rounded-xl flex items-center gap-3 animate-scale-in"
          style={{
            background: importStatus.type === "success" ? "var(--mood-5-soft)" : "oklch(0.93 0.06 27)",
            border: `1px solid ${importStatus.type === "success" ? "var(--mood-5)" : "var(--destructive)"}`,
          }}
        >
          <span>{importStatus.type === "success" ? "✅" : "⚠️"}</span>
          <p className="text-sm" style={{ color: "var(--foreground)" }}>
            {importStatus.message}
          </p>
        </div>
      )}

      {/* Info card */}
      <div className="px-5 mb-5">
        <div
          className="p-4 rounded-2xl"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <h3
            className="text-xs font-medium uppercase tracking-widest mb-3"
            style={{ color: "var(--muted-foreground)" }}
          >
            Ваш дневник
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                Всего записей
              </span>
              <span className="text-sm font-semibold" style={{ color: "var(--foreground)" }}>
                {entries.length}
              </span>
            </div>
            <div style={{ height: "1px", background: "var(--border)" }} />
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                Период
              </span>
              <span className="text-sm font-medium" style={{ color: "var(--foreground)" }}>
                {dateRange}
              </span>
            </div>
            <div style={{ height: "1px", background: "var(--border)" }} />
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                Размер данных
              </span>
              <span className="text-sm font-medium" style={{ color: "var(--foreground)" }}>
                {storageSizeStr}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 mb-5">
        <div
          className="rounded-2xl overflow-hidden"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          {/* Export */}
          <button
            onClick={handleExport}
            className="w-full flex items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-secondary"
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "var(--mood-5-soft)" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--mood-5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium" style={{ color: "var(--foreground)" }}>
                Экспорт в JSON
              </div>
              <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Скачать все записи одним файлом
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <div className="mx-4" style={{ height: "1px", background: "var(--border)" }} />

          {/* Import */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-secondary"
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "var(--mood-4-soft)" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--mood-4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium" style={{ color: "var(--foreground)" }}>
                Импорт из JSON
              </div>
              <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Загрузить записи из файла
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleImport}
            className="hidden"
          />
        </div>
      </div>

      {/* Export info */}
      {showExportInfo && (
        <div
          className="mx-5 mb-5 px-4 py-3 rounded-xl flex items-center gap-3 animate-scale-in"
          style={{ background: "var(--mood-5-soft)", border: "1px solid var(--mood-5)" }}
        >
          <span>📥</span>
          <p className="text-sm" style={{ color: "var(--foreground)" }}>
            Файл сохранён в папку загрузок
          </p>
        </div>
      )}

      {/* Danger zone */}
      {entries.length > 0 && (
        <div className="px-5 mb-5">
          <div
            className="p-4 rounded-2xl"
            style={{
              background: "oklch(0.97 0.015 27)",
              border: "1px solid oklch(0.9 0.04 27)",
            }}
          >
            <h3
              className="text-xs font-medium uppercase tracking-widest mb-3"
              style={{ color: "var(--destructive)" }}
            >
              Опасная зона
            </h3>
            <p className="text-xs mb-3" style={{ color: "var(--muted-foreground)" }}>
              Удаление всех записей нельзя отменить. Рекомендуем сначала экспортировать данные.
            </p>
            <button
              onClick={handleClearAll}
              className="text-xs font-medium px-4 py-2 rounded-xl transition-all hover:scale-[0.98]"
              style={{
                background: "oklch(0.577 0.245 27.325 / 0.1)",
                color: "var(--destructive)",
                border: "1px solid oklch(0.577 0.245 27.325 / 0.2)",
              }}
            >
              Удалить все записи
            </button>
          </div>
        </div>
      )}

      {/* About */}
      <div className="px-5 pb-6">
        <div className="text-center py-4">
          <div className="text-2xl mb-2">📔</div>
          <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            Дневник настроения v1.0
          </p>
          <p className="text-[10px] mt-1" style={{ color: "var(--muted-foreground)", opacity: 0.6 }}>
            Все данные хранятся локально в вашем браузере
          </p>
        </div>
      </div>
    </div>
  );
}
